import { FC, useContext, useRef, useState } from 'react'
import dayjs from 'dayjs'
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined'
import { Box, Modal, Button, Typography, IconButton, useTheme } from '@mui/material'
import {
  getAllHopsets,
  getAllSFAFAntennas,
  getAllSFAFContents,
  getAllSFAFEquipments,
  getAllSFAFExcludedFrequencies,
  getAllSFAFFrequencies,
  getAllSFAFStationClasses,
} from '../../../../services/spectrum-manager.service'
import { AuthContext } from '../../../../contexts/auth.context'
import { generateSFAF } from '../../../../SFAF/sfaf-generator'
import { SpectrumManagerContext } from '../../../../contexts/spectrum.context'

import styles from './styles.module.scss'

interface IProps {
  isOpen: boolean
  frequency_group_id: number
  onClose: () => void
}

const AllSFAFsModal: FC<IProps> = ({ isOpen, frequency_group_id, onClose }) => {
  const theme = useTheme()

  const [sfaf, setSfaf] = useState('')

  const { user } = useContext(AuthContext)
  const { filterOptions } = useContext(SpectrumManagerContext)

  const linkRef = useRef<HTMLAnchorElement>(null)

  const handleExport = () => {
    if (linkRef.current && sfaf) {
      const file = new Blob([sfaf], { type: 'text/plain' })
      linkRef.current.href = URL.createObjectURL(file)
      linkRef.current.download = 'sfaf.txt'

      linkRef.current.click()
    }
  }

  const handleSFAFGenerate = async () => {
    const sfafAntennas = await getAllSFAFAntennas(filterOptions)
    const sfafContents = await getAllSFAFContents(filterOptions)
    const sfafEquipments = await getAllSFAFEquipments(filterOptions)
    const sfafFrequencies = await getAllSFAFFrequencies({
      ...filterOptions,
      requestFrequencyGroupId: frequency_group_id,
    })
    const sfafStationClasses = await getAllSFAFStationClasses(filterOptions)
    const sfafExcludedFrequencies = await getAllSFAFExcludedFrequencies(filterOptions)
    const hopsets = await getAllHopsets()
    const sfafAgencyOverride = user?.agencyOverride

    const parsedSfafFrequencies = sfafFrequencies?.data?.map((frequency) => ({
      ...frequency,
      start_date: frequency.start_date && dayjs(frequency.start_date).utc(true),
      end_date: frequency.end_date && dayjs(frequency.end_date).utc(true),
    }))

    if (
      parsedSfafFrequencies &&
      sfafEquipments.data &&
      sfafContents.data &&
      sfafStationClasses.data &&
      sfafExcludedFrequencies.data &&
      sfafAntennas.data &&
      hopsets.data
    ) {
      const sfaf = await generateSFAF(
        parsedSfafFrequencies,
        sfafContents.data,
        sfafExcludedFrequencies.data,
        sfafEquipments.data,
        hopsets.data,
        sfafStationClasses.data,
        sfafAntennas.data,
        sfafAgencyOverride,
      )

      setSfaf(sfaf)
    }
  }

  return (
    <Modal className={styles.modal} open={isOpen} onClose={onClose}>
      <Box className={styles.modalContent}>
        <Box>
          <Box className={styles.header}>
            <Typography data-testid="title" id="enableTitle" className={styles.title}>
              Generate All SFAFs
            </Typography>
            <IconButton data-testid="closeButton" onClick={onClose}>
              <CloseOutlinedIcon />
            </IconButton>
          </Box>
          <Box className={styles.content}>
            <Box className={styles.actions}>
              <Button onClick={handleSFAFGenerate}>Generate</Button>
              <Button variant="outlined" onClick={handleExport}>
                Export SFAF
              </Button>
            </Box>
            <textarea
              placeholder="SFAF value"
              style={{
                fontSize: 14,
                marginTop: 20,
                width: '100%',
                height: '300px',
                padding: '5px',
                color: theme.palette.text.primary,
                backgroundColor: 'transparent',
              }}
              value={sfaf}
            />
            <a ref={linkRef} hidden />
          </Box>
        </Box>
      </Box>
    </Modal>
  )
}

export default AllSFAFsModal
